import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import { Activity, Globe2, Building2, Boxes, Library, Volume2, VolumeX, RotateCcw, Maximize, Minimize, Code2, Copy, Check } from "lucide-react";
import { toast } from "sonner";
import { useSim } from "../lib/store";
import { xrStore } from "../three/xrStore";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";

export function TopBar() {
  const viewMode = useSim((s) => s.viewMode);
  const setViewMode = useSim((s) => s.setViewMode);
  const epicenter = useSim((s) => s.epicenter);
  const setDrawerOpen = useSim((s) => s.setDrawerOpen);
  const audioOn = useSim((s) => s.audioOn);
  const setAudioOn = useSim((s) => s.setAudioOn);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [embedOpen, setEmbedOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const shareUrl = typeof window !== "undefined" ? window.location.origin : "";
  const embedCode = `<iframe
  src="${shareUrl}"
  width="100%"
  height="720"
  style="border:0;border-radius:12px;overflow:hidden;"
  allow="xr-spatial-tracking; accelerometer; gyroscope; fullscreen"
  allowfullscreen
  title="Anatomy of an Earthquake"
></iframe>`;

  const copyEmbed = async () => {
    try {
      await navigator.clipboard.writeText(embedCode);
      setCopied(true);
      toast.success("Embed code copied", { description: "Paste the <iframe> into your site's HTML." });
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      toast.error("Couldn't copy — select the code and copy manually");
    }
  };

  useEffect(() => {
    const onChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, []);

  // Press "F" to toggle fullscreen (ignored while typing in a field).
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "f" && e.key !== "F") return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const t = e.target;
      if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.isContentEditable)) return;
      e.preventDefault();
      if (!document.fullscreenElement) document.documentElement.requestFullscreen().catch(() => {});
      else document.exitFullscreen().catch(() => {});
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const toggleFullscreen = async () => {
    try {
      if (!document.fullscreenElement) {
        await document.documentElement.requestFullscreen();
      } else {
        await document.exitFullscreen();
      }
    } catch (e) {
      toast.error("Fullscreen unavailable in this browser");
    }
  };

  const enterXR = async () => {
    try {
      await xrStore.enterVR();
    } catch (e) {
      toast.error("Immersive VR unavailable on this device", {
        description: "Connect a Quest 3 / Vision Pro headset or open in an XR-capable browser.",
      });
    }
  };

  return (
    <motion.header
      data-testid="main-title-header"
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className="fixed top-0 left-0 right-0 z-[70] flex items-start justify-between gap-3 px-4 sm:px-6 pt-5 pb-3 backdrop-blur-xl bg-zinc-950/60 border-b border-white/10"
    >
      {/* left branding column: seal + title, with the view toggle beneath */}
      <div className="flex flex-col gap-3 min-w-0">
        <div className="flex items-center gap-3 min-w-0">
          <img
            src="/tab-seal.png"
            alt="The Academic Boardroom seal"
            data-testid="tab-seal"
            className="h-10 w-10 rounded-full shrink-0 ring-1 ring-[#D4AF37]/50 shadow-[0_0_16px_rgba(212,175,55,0.35)]"
          />
          <div className="min-w-0">
            <h1 className="font-display text-sm sm:text-lg font-bold tracking-tight text-white uppercase leading-none whitespace-nowrap">
              Anatomy of an Earthquake
            </h1>
            <div className="mt-1 flex items-center gap-3">
              <span data-testid="brand-title" className="font-serif-gold text-[10px] sm:text-sm font-extrabold tracking-wide leading-none whitespace-nowrap" style={{ color: "#D4AF37" }}>
                THE ACADEMIC BOARDROOM
              </span>
              <span className="hidden sm:flex items-center gap-1 text-[9px] font-mono-tele uppercase tracking-[0.2em] text-emerald-400">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" /> USGS Live
              </span>
            </div>
          </div>
        </div>

        {/* view switch — left-aligned with the seal, forming a clean branding column */}
        <div data-testid="view-toggle-group" className="flex items-center gap-1 p-1 rounded-xl bg-black/40 border border-white/10 w-fit">
          <button
            data-testid="view-macro-globe-btn"
            onClick={() => setViewMode("macro")}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
              viewMode === "macro" ? "bg-red-600/90 text-white shadow-[0_0_16px_rgba(239,68,68,0.4)]" : "text-zinc-400 hover:text-white"
            }`}
          >
            <Globe2 className="h-3.5 w-3.5" /> Macro Globe
          </button>
          <button
            data-testid="view-street-sandbox-btn"
            onClick={() => {
              if (!epicenter) return toast.error("Drop an epicenter pin first");
              setViewMode("street");
            }}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
              viewMode === "street" ? "bg-red-600/90 text-white shadow-[0_0_16px_rgba(239,68,68,0.4)]" : "text-zinc-400 hover:text-white"
            } ${!epicenter ? "opacity-40 cursor-not-allowed" : ""}`}
          >
            <Building2 className="h-3.5 w-3.5" /> 1:1 Street
          </button>
        </div>
      </div>

      {/* right */}
      <div className="flex items-center gap-2">
        <button
          data-testid="camera-reset-button"
          onClick={() => {
            setViewMode("macro");
            toast("View reset to macro globe");
          }}
          title="Reset view"
          className="p-2 rounded-lg bg-white/5 hover:bg-white/10 text-zinc-300 hover:text-white border border-white/10 transition-colors"
        >
          <RotateCcw className="h-4 w-4" />
        </button>
        <button
          data-testid="audio-toggle-button"
          onClick={() => setAudioOn(!audioOn)}
          title="Seismic sonification"
          className={`p-2 rounded-lg border transition-colors ${
            audioOn ? "bg-red-600/20 border-red-500/40 text-red-300" : "bg-white/5 border-white/10 text-zinc-300 hover:text-white"
          }`}
        >
          {audioOn ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
        </button>
        <button
          data-testid="fullscreen-toggle-button"
          onClick={toggleFullscreen}
          title={isFullscreen ? "Exit fullscreen (F)" : "Enter fullscreen (F)"}
          className="hidden sm:inline-flex p-2 rounded-lg bg-white/5 hover:bg-white/10 text-zinc-300 hover:text-white border border-white/10 transition-colors"
        >
          {isFullscreen ? <Minimize className="h-4 w-4" /> : <Maximize className="h-4 w-4" />}
        </button>
        <button
          data-testid="xr-enter-mode-button"
          onClick={enterXR}
          className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-zinc-200 hover:text-white border border-white/10 transition-colors text-xs font-medium"
        >
          <Boxes className="h-4 w-4" /> <span className="hidden sm:inline">Enter VR</span>
        </button>
        <button
          data-testid="embed-code-button"
          onClick={() => setEmbedOpen(true)}
          title="Get embed code for your site"
          className="hidden sm:inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-zinc-200 hover:text-white border border-white/10 transition-colors text-xs font-medium"
        >
          <Code2 className="h-4 w-4" /> <span className="hidden md:inline">Embed</span>
        </button>
        <button
          data-testid="scenarios-drawer-trigger"
          onClick={() => setDrawerOpen(true)}
          className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-gradient-to-r from-red-600 to-red-700 text-white border border-red-400/40 shadow-[0_0_20px_rgba(239,68,68,0.4)] hover:from-red-500 hover:to-red-600 transition-all text-xs font-semibold"
        >
          <Library className="h-4 w-4" /> <span className="hidden sm:inline">Scenarios</span>
        </button>
      </div>

      <Dialog open={embedOpen} onOpenChange={setEmbedOpen}>
        <DialogContent data-testid="embed-dialog" className="bg-zinc-950 border-white/10 text-white sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-display uppercase tracking-tight">
              <Code2 className="h-5 w-5 text-red-400" /> Embed on your site
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              Paste this snippet into your website's HTML to embed the full interactive experience.
            </DialogDescription>
          </DialogHeader>
          <div className="rounded-xl bg-black/60 border border-white/10 p-3 font-mono-tele text-[11px] leading-relaxed text-emerald-200 whitespace-pre-wrap break-all select-all">
            {embedCode}
          </div>
          <button
            data-testid="embed-copy-button"
            onClick={copyEmbed}
            className="flex items-center justify-center gap-2 w-full px-4 py-2.5 rounded-lg bg-gradient-to-r from-red-600 to-red-700 text-white border border-red-400/40 hover:from-red-500 hover:to-red-600 transition-all text-sm font-semibold"
          >
            {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
            {copied ? "Copied!" : "Copy embed code"}
          </button>
          <p className="text-[11px] text-zinc-500 leading-relaxed">
            Uses this page's URL (<span className="text-zinc-300">{shareUrl}</span>). For a public embed, copy this from your deployed production site. If your site blocks the frame, ensure framing is allowed for the domain.
          </p>
        </DialogContent>
      </Dialog>
    </motion.header>
  );
}
